import { ArrowLeft, Calendar, ThumbsUp, ThumbsDown, Share2, Printer, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetPublicArticleByIdQuery } from '../../store/apis/helpCenter.api';
import { Button, Badge } from '../common';
import { HelpArticleSkeleton, ShimmerBlock } from './Skeleton';
import { cn } from '../../utils/cn';
import { MarkdownContent } from './MarkdownContent';

import { scrollToTop } from '../survey/hooks';

interface HelpArticleProps {
    articleId?: string;
    onBack?: () => void;
    onContactSupport?: (moduleCode?: string, articleId?: string) => void;
}

export function HelpArticle({ articleId, onBack, onContactSupport }: HelpArticleProps) {
    const { id } = useParams();
    const navigate = useNavigate();

    const activeId = id || articleId;
    const { data: article, isLoading } = useGetPublicArticleByIdQuery(activeId!);
    const [feedback, setFeedback] = useState<'helpful' | 'not-helpful' | null>(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Reset image loaded state and scroll to top when article changes
    useEffect(() => {
        setImageLoaded(false);
        scrollToTop();
    }, [activeId]);

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    const handleContactSupport = () => {
        if (onContactSupport) {
            onContactSupport(article?.moduleCode, article?._id);
        } else {
            navigate('/help-center/contact', {
                state: {
                    moduleCode: article?.moduleCode,
                    articleId: article?._id
                }
            });
        }
    };

    // Atomic loading state: Ready only when data is here AND image is loaded (if image exists)
    const isPageReady = !isLoading && article && (!article.imageUrl || imageLoaded);

    return (
        <div className="bg-surface-muted p-4 sm:p-6 lg:p-8 min-h-screen">
            {(!isPageReady) ? (
                <div className="mx-auto py-12">
                    <HelpArticleSkeleton />
                    {/* Hidden image to trigger loading in background */}
                    {article?.imageUrl && (
                        <img
                            src={article.imageUrl}
                            onLoad={() => setImageLoaded(true)}
                            className="hidden"
                            alt=""
                        />
                    )}
                </div>
            ) : !article ? (
                <div className="max-w-3xl mx-auto text-center py-12 bg-white rounded-xl border border-dashed border-slate-200">
                    <h2 className="text-xl font-bold text-slate-900 mb-1">Article not found</h2>
                    <p className="text-xs text-slate-500 mb-6">The article you're looking for might have been moved or deleted.</p>
                    <Button
                        onClick={handleBack}
                        appearance="ghost"
                        size="small"
                        className="text-slate-500"
                        icon={<ArrowLeft size={16} />}
                    >
                        Back to Help Center
                    </Button>
                </div>
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                >
                    {/* Article Header */}
                    <header className="mb-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                                <Badge variant="blue" size="middle">
                                    Article
                                </Badge>
                            </div>
                            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-3">
                                {article.title}
                            </h1>
                            <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                                <Calendar size={10} />
                                <span>
                                    Last updated {article.updatedAt
                                        ? (() => {
                                            const date = new Date(article.updatedAt);
                                            const d = String(date.getDate()).padStart(2, '0');
                                            const m = String(date.getMonth() + 1).padStart(2, '0');
                                            const y = date.getFullYear();
                                            return `${d}/${m}/${y}`;
                                        })()
                                        : 'Recently'}
                                </span>
                            </div>
                        </div>
                        <Button
                            appearance="ghost"
                            onClick={handleBack}
                            size="small"
                            className="text-slate-500 self-start"
                            icon={<ArrowLeft size={16} />}
                        >
                            Back
                        </Button>
                    </header>

                    {/* Content Image */}
                    {article.imageUrl && (
                        <div className="mb-10 relative w-full overflow-hidden rounded-xl border border-slate-100 bg-slate-50/50 shadow-sm flex items-center justify-center min-h-[200px] md:min-h-[400px]">
                            <img
                                src={article.imageUrl}
                                alt={article.title}
                                className="max-w-full max-h-[600px] w-auto h-auto object-contain"
                            />
                        </div>
                    )}

                    {/* Article Content — rendered Markdown */}
                    <article className="mb-12 rounded-xl border border-slate-100 bg-white p-6 sm:p-8 shadow-soft">
                        <MarkdownContent content={article.content} />
                    </article>

                    {/* Footer */}
                    <footer className="pt-10 border-t border-slate-100">
                        <div className="w-full flex flex-col md:flex-row items-center justify-between gap-6 py-2">
                            <div className="text-center md:text-left flex-1">
                                <p className="text-sm  text-slate-900 leading-relaxed max-w-2xl">
                                    Need help navigating things? Reach out to our support team for quick and reliable assistance.
                                </p>
                            </div>
                            <Button
                                onClick={handleContactSupport}
                                appearance="primary"
                                size="small"
                                className="w-full md:w-fit px-6 flex-shrink-0"
                            >
                                Contact Support
                            </Button>
                        </div>
                    </footer>
                </motion.div>
            )}
        </div>
    );
}
